import { local } from './storage';
export const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  /** The server's JSON answer, for the fields beyond `error` (e.g. needs_target_review). */
  data: Record<string, unknown> | null;

  constructor(message: string, status: number, data: Record<string, unknown> | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.body && !(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  headers.set('X-NL-AOS-Request', '1');

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...options, credentials: 'include', headers });
  } catch (reason) {
    if (options.signal?.aborted) throw reason;
    throw new ApiError('The service could not be reached. Check your connection and try again.', 0);
  }

  const payload = await response.json().catch(() => null) as { error?: string } | null;
  if (!response.ok) {
    if (response.status === 401 && path !== '/api/auth/login') {
      local.remove('nl-aos-token');
      local.remove('nl-aos-user');
      window.dispatchEvent(new Event('nl-aos-session-expired'));
    }
    throw new ApiError(payload?.error || `Request failed (${response.status})`, response.status, payload as Record<string, unknown> | null);
  }
  return payload as T;
}

/** "$5,859.18", or compact "$5.9K" (one decimal, so $1.5K never reads as $2K). */
export function formatMoney(value: number | null | undefined, compact = false) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (!compact) return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.abs(value) < 1000 ? 0 : 1,
  }).format(value);
}

/** The agency's clock: every time the app shows is US Eastern, whoever is looking. */
export const AGENCY_TZ = 'America/New_York';

export function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])));
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  // A timestamp is shown as the agency's calendar day.
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: AGENCY_TZ }).format(date);
}

/** "Oct 2, 2026, 11:15 PM ET": one way to show a moment, on the agency's clock. */
export function formatDateTime(value: string | number | Date | null | undefined) {
  if (value === null || value === undefined || value === '') return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: AGENCY_TZ }).format(date)} ET`;
}

/** "+15595550900" → "(559) 555-0900". Anything that is not a US number is shown as given. */
export function formatPhone(value: string | null | undefined) {
  if (!value) return '—';
  const d = String(value).replace(/\D/g, '');
  const us = d.length === 11 && d.startsWith('1') ? d.slice(1) : d.length === 10 ? d : null;
  return us ? `(${us.slice(0, 3)}) ${us.slice(3, 6)}-${us.slice(6)}` : String(value);
}
